// src/services/analytics.ts
// Centrale analytics service voor GTM dataLayer events

// TypeScript declarations voor dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Zorg dat dataLayer bestaat
const getDataLayer = (): any[] => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }
  return [];
};

// ============================================
// HELPER FUNCTIE
// ============================================

const pushEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  const dataLayer = getDataLayer();
  
  const eventData = {
    event: eventName,
    ...eventParams,
  };
  
  dataLayer.push(eventData);
  
  // Debug logging (verwijder in productie of maak configureerbaar)
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Analytics Event:', eventData);
  }
};

// ============================================
// AUTHENTICATION EVENTS
// ============================================

/**
 * Track wanneer een nieuwe gebruiker zich registreert
 */
export const trackSignUp = (
  method: 'email' | 'google' = 'email',
  plan?: 'free' | 'pro' | 'products',
  billing?: 'monthly' | 'yearly',
  source?: 'pricing_page' | 'hero' | 'navbar' | 'auth_modal'
) => {
  pushEvent('sign_up', {
    method,
    plan: plan || 'free',
    billing_cycle: billing || null,
    registration_source: source || 'unknown',
  });
};

/**
 * Track wanneer een gebruiker inlogt
 */
export const trackLogin = (method: 'email' | 'google' = 'email') => {
  pushEvent('login', {
    method,
  });
};

/**
 * Track wanneer een gebruiker uitlogt
 */
export const trackLogout = () => {
  pushEvent('logout');
};

// ============================================
// E-COMMERCE / PURCHASE EVENTS
// ============================================

/**
 * Track wanneer gebruiker checkout start
 */
export const trackBeginCheckout = (params: {
  productId: string;
  productName: string;
  price: number; // in euros (niet centen)
  productType: 'credits' | 'subscription' | 'summary' | 'ai_pack';
}) => {
  pushEvent('begin_checkout', {
    currency: 'EUR',
    value: params.price,
    items: [{
      item_id: params.productId,
      item_name: params.productName,
      price: params.price,
      item_category: params.productType,
      quantity: 1,
    }],
  });
};

/**
 * Track succesvolle aankoop
 */
export const trackPurchase = (params: {
  transactionId: string;
  productId: string;
  productName: string;
  price: number; // in euros
  productType: 'credits' | 'subscription' | 'summary' | 'ai_pack';
}) => {
  pushEvent('purchase', {
    transaction_id: params.transactionId,
    currency: 'EUR',
    value: params.price,
    items: [{
      item_id: params.productId,
      item_name: params.productName,
      price: params.price,
      item_category: params.productType,
      quantity: 1,
    }],
  });
};

// ============================================
// EXAM EVENTS
// ============================================

/**
 * Track wanneer gebruiker een proefexamen start
 */
export const trackExamStart = (params: {
  examType: string;
  questionCount?: number;
}) => {
  pushEvent('exam_start', {
    exam_type: params.examType,
    question_count: params.questionCount,
  });
};

/**
 * Track wanneer gebruiker een proefexamen voltooit
 */
export const trackExamComplete = (params: {
  examType: string;
  score: number; // percentage 0-100
  passed: boolean;
  questionCount: number;
  timeSpentSeconds?: number;
}) => {
  pushEvent('exam_complete', {
    exam_type: params.examType,
    score: params.score,
    passed: params.passed,
    question_count: params.questionCount,
    time_spent_seconds: params.timeSpentSeconds,
  });
};

/**
 * Track wanneer gebruiker een proefexamen HAALT
 * Gebruikt voor Google Ads conversie tracking
 */
export const trackExamPassed = (params: {
  examType: string;
  score: number;
  questionCount: number;
  timeSpentSeconds?: number;
}) => {
  pushEvent('exam_passed', {
    exam_type: params.examType,
    score: params.score,
    question_count: params.questionCount,
    time_spent_seconds: params.timeSpentSeconds,
  });
};

/**
 * Track wanneer gebruiker een proefexamen NIET haalt
 * Gebruikt voor remarketing / retargeting
 */
export const trackExamFailed = (params: {
  examType: string;
  score: number;
  questionCount: number;
  timeSpentSeconds?: number;
}) => {
  pushEvent('exam_failed', {
    exam_type: params.examType,
    score: params.score,
    question_count: params.questionCount,
    time_spent_seconds: params.timeSpentSeconds,
  });
};

// ============================================
// AI QUESTION EVENTS
// ============================================

/**
 * Track wanneer gebruiker AI vragen genereert
 */
export const trackAIQuestionsGenerated = (params: {
  examType: string;
  topic: string;
  questionCount: number;
}) => {
  pushEvent('ai_questions_generated', {
    exam_type: params.examType,
    topic: params.topic,
    question_count: params.questionCount,
  });
};

/**
 * Track wanneer gebruiker AI quiz voltooit
 */
export const trackAIQuizComplete = (params: {
  examType: string;
  topic: string;
  score: number;
  questionCount: number;
}) => {
  pushEvent('ai_quiz_complete', {
    exam_type: params.examType,
    topic: params.topic,
    score: params.score,
    question_count: params.questionCount,
  });
};

// ============================================
// GAME EVENTS
// ============================================

// Game types - uitgebreid met arcade games
type GameType = 'definition_dash' | 'wft_blitz' | 'duel' | 'schade_survivor';

/**
 * Track wanneer gebruiker een game start
 */
export const trackGameStart = (params: {
  gameType: GameType;
  examType?: string;
  isMobile?: boolean;
}) => {
  pushEvent('game_start', {
    game_type: params.gameType,
    exam_type: params.examType,
    is_mobile: params.isMobile,
  });
};

/**
 * Track wanneer gebruiker een game voltooit
 */
export const trackGameComplete = (params: {
  gameType: GameType;
  score: number;
  examType?: string;
  distance?: number;
  coins?: number;
  durationSeconds?: number;
  isNewHighScore?: boolean;
  isMobile?: boolean;
}) => {
  pushEvent('game_complete', {
    game_type: params.gameType,
    score: params.score,
    exam_type: params.examType,
    distance: params.distance,
    coins: params.coins,
    duration_seconds: params.durationSeconds,
    is_new_high_score: params.isNewHighScore,
    is_mobile: params.isMobile,
  });
};

// ============================================
// ARCADE GAME SPECIFIC EVENTS
// ============================================

/**
 * Track nieuwe high score in arcade game
 */
export const trackArcadeHighScore = (params: {
  gameType: GameType;
  score: number;
  previousHighScore: number;
}) => {
  pushEvent('arcade_high_score', {
    game_type: params.gameType,
    score: params.score,
    previous_high_score: params.previousHighScore,
  });
};

/**
 * Track level up
 */
export const trackLevelUp = (params: {
  oldLevel: number;
  newLevel: number;
  gameType?: GameType;
}) => {
  pushEvent('level_up', {
    old_level: params.oldLevel,
    new_level: params.newLevel,
    game_type: params.gameType,
  });
};

/**
 * Track badge earned
 */
export const trackBadgeEarned = (params: {
  badgeId: string;
  gameType?: GameType;
}) => {
  pushEvent('badge_earned', {
    badge_id: params.badgeId,
    game_type: params.gameType,
  });
};

// ============================================
// STUDY EVENTS
// ============================================

/**
 * Track wanneer gebruiker samenvatting bekijkt
 */
export const trackSummaryView = (params: {
  examType: string;
  moduleCode: string;
}) => {
  pushEvent('summary_view', {
    exam_type: params.examType,
    module_code: params.moduleCode,
  });
};

// ============================================
// PAGE VIEW (voor SPA navigatie)
// ============================================

/**
 * Track virtuele page view voor SPA
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  pushEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

// ============================================
// PRICING & REGISTRATION EVENTS
// ============================================

/**
 * Track pricing page interacties
 */
export const trackPricingClick = (params: {
  plan: 'free' | 'pro' | 'products';
  billing?: 'monthly' | 'yearly';
  buttonLocation: 'pricing_section' | 'hero' | 'final_cta';
}) => {
  pushEvent('pricing_click', {
    plan: params.plan,
    billing_cycle: params.billing || null,
    button_location: params.buttonLocation,
  });
};

/**
 * Track register page view
 */
export const trackRegisterPageView = (params: {
  plan: 'free' | 'pro' | 'products';
  billing?: 'monthly' | 'yearly';
  referrer?: string;
}) => {
  pushEvent('register_page_view', {
    plan: params.plan,
    billing_cycle: params.billing || null,
    referrer: params.referrer || document.referrer || 'direct',
  });
};

/**
 * Track registratie form abandonment
 */
export const trackRegisterAbandonment = (params: {
  plan: 'free' | 'pro' | 'products';
  fieldsCompleted: number;
  timeOnPage: number;
}) => {
  pushEvent('register_abandonment', {
    plan: params.plan,
    fields_completed: params.fieldsCompleted,
    time_on_page_seconds: params.timeOnPage,
  });
};

// ============================================
// FAQ EVENTS
// ============================================

/**
 * Track wanneer FAQ pagina wordt bekeken
 */
export const trackFAQPageView = () => {
  pushEvent('faq_page_view', {});
};

/**
 * Track wanneer een FAQ vraag wordt geopend
 */
export const trackFAQClick = (params: {
  question: string;
  category: string;
}) => {
  pushEvent('faq_click', {
    faq_question: params.question.substring(0, 100),
    faq_category: params.category,
  });
};

// ============================================
// CONTENT / KENNISBANK EVENTS (NIEUW)
// ============================================

/**
 * Track artikel page view
 */
export const trackArticleView = (params: {
  articleId: string;
  articleSlug: string;
  articleTitle: string;
  category: string;
  wordCount: number;
  readTimeMinutes: number;
}) => {
  pushEvent('article_view', {
    article_id: params.articleId,
    article_slug: params.articleSlug,
    article_title: params.articleTitle.substring(0, 100),
    article_category: params.category,
    word_count: params.wordCount,
    read_time_minutes: params.readTimeMinutes,
    content_type: 'kennisbank',
  });
};

/**
 * Track scroll depth op artikel (25%, 50%, 75%, 100%)
 */
export const trackArticleScrollDepth = (params: {
  articleId: string;
  articleSlug: string;
  depthPercent: 25 | 50 | 75 | 100;
}) => {
  pushEvent('article_scroll_depth', {
    article_id: params.articleId,
    article_slug: params.articleSlug,
    depth_percent: params.depthPercent,
    content_type: 'kennisbank',
  });
};

/**
 * Track tijd op artikel pagina
 */
export const trackArticleTimeOnPage = (params: {
  articleId: string;
  articleSlug: string;
  timeSeconds: number;
}) => {
  pushEvent('article_time_on_page', {
    article_id: params.articleId,
    article_slug: params.articleSlug,
    time_seconds: params.timeSeconds,
    content_type: 'kennisbank',
  });
};

/**
 * Track Table of Contents klik
 */
export const trackArticleTOCClick = (params: {
  articleId: string;
  sectionId: string;
  sectionTitle: string;
}) => {
  pushEvent('article_toc_click', {
    article_id: params.articleId,
    section_id: params.sectionId,
    section_title: params.sectionTitle.substring(0, 50),
    content_type: 'kennisbank',
  });
};

/**
 * Track FAQ accordion expand in artikel
 */
export const trackArticleFAQExpand = (params: {
  articleId: string;
  question: string;
  faqIndex: number;
}) => {
  pushEvent('article_faq_expand', {
    article_id: params.articleId,
    faq_question: params.question.substring(0, 100),
    faq_index: params.faqIndex,
    content_type: 'kennisbank',
  });
};

/**
 * Track CTA klik binnen artikel
 */
export const trackArticleCTAClick = (params: {
  articleId: string;
  articleSlug: string;
  ctaType: 'inline' | 'sidebar' | 'bottom' | 'header' | 'related_module';
  ctaText: string;
  destination: string;
}) => {
  pushEvent('article_cta_click', {
    article_id: params.articleId,
    article_slug: params.articleSlug,
    cta_type: params.ctaType,
    cta_text: params.ctaText.substring(0, 50),
    destination: params.destination,
    content_type: 'kennisbank',
  });
};

/**
 * Track gerelateerd artikel klik
 */
export const trackRelatedArticleClick = (params: {
  fromArticleId: string;
  fromSlug: string;
  toArticleId: string;
  toSlug: string;
  position: number;
}) => {
  pushEvent('related_article_click', {
    from_article_id: params.fromArticleId,
    from_slug: params.fromSlug,
    to_article_id: params.toArticleId,
    to_slug: params.toSlug,
    position: params.position,
    content_type: 'kennisbank',
  });
};

/**
 * Track kennisbank overview pagina
 */
export const trackKennisbankView = () => {
  pushEvent('kennisbank_view', {
    content_type: 'kennisbank',
  });
};

/**
 * Track categorie pagina view
 */
export const trackCategoryView = (params: {
  category: string;
  articleCount: number;
}) => {
  pushEvent('category_view', {
    category: params.category,
    article_count: params.articleCount,
    content_type: 'kennisbank',
  });
};

/**
 * Track artikel klik vanuit lijst/overview
 */
export const trackArticleClickFromList = (params: {
  articleId: string;
  articleSlug: string;
  listType: 'category' | 'overview' | 'recent' | 'related' | 'search';
  position: number;
}) => {
  pushEvent('article_click_from_list', {
    article_id: params.articleId,
    article_slug: params.articleSlug,
    list_type: params.listType,
    position: params.position,
    content_type: 'kennisbank',
  });
};

// ============================================
// CONTENT TRACKING HELPERS
// ============================================

/**
 * Helper: Maak scroll depth tracker
 * Retourneert start/stop functies
 */
export const createScrollDepthTracker = (
  articleId: string,
  articleSlug: string
): { start: () => void; stop: () => void } => {
  let trackedDepths: Set<number> = new Set();
  let scrollHandler: (() => void) | null = null;

  const calculateScrollDepth = (): number => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    
    const scrollableHeight = documentHeight - windowHeight;
    if (scrollableHeight <= 0) return 100;
    
    return Math.min(100, Math.round((scrollTop / scrollableHeight) * 100));
  };

  const checkAndTrackDepth = () => {
    const depth = calculateScrollDepth();
    
    const thresholds: (25 | 50 | 75 | 100)[] = [25, 50, 75, 100];
    thresholds.forEach(threshold => {
      if (depth >= threshold && !trackedDepths.has(threshold)) {
        trackedDepths.add(threshold);
        trackArticleScrollDepth({
          articleId,
          articleSlug,
          depthPercent: threshold,
        });
      }
    });
  };

  const start = () => {
    trackedDepths = new Set();
    scrollHandler = () => {
      requestAnimationFrame(checkAndTrackDepth);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    checkAndTrackDepth();
  };

  const stop = () => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
  };

  return { start, stop };
};

/**
 * Helper: Maak time on page tracker
 * Track tijd op 30s, 60s, 120s, 300s
 */
export const createTimeOnPageTracker = (
  articleId: string,
  articleSlug: string
): { start: () => void; stop: () => void } => {
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let elapsedSeconds = 0;
  const trackIntervals = [30, 60, 120, 300];

  const start = () => {
    elapsedSeconds = 0;
    intervalId = setInterval(() => {
      elapsedSeconds += 1;
      if (trackIntervals.includes(elapsedSeconds)) {
        trackArticleTimeOnPage({
          articleId,
          articleSlug,
          timeSeconds: elapsedSeconds,
        });
      }
    }, 1000);
  };

  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  return { start, stop };
};

// ============================================
// EXPORT DEFAULT OBJECT
// ============================================

const analytics = {
  // Auth
  trackSignUp,
  trackLogin,
  trackLogout,
  // E-commerce
  trackBeginCheckout,
  trackPurchase,
  // Exams
  trackExamStart,
  trackExamComplete,
  trackExamPassed,
  trackExamFailed,
  // AI
  trackAIQuestionsGenerated,
  trackAIQuizComplete,
  // Games
  trackGameStart,
  trackGameComplete,
  // Arcade
  trackArcadeHighScore,
  trackLevelUp,
  trackBadgeEarned,
  // Study
  trackSummaryView,
  // Navigation
  trackPageView,
  // Pricing & Registration
  trackPricingClick,
  trackRegisterPageView,
  trackRegisterAbandonment,
  // FAQ
  trackFAQPageView,
  trackFAQClick,
  // Content / Kennisbank (NIEUW)
  trackArticleView,
  trackArticleScrollDepth,
  trackArticleTimeOnPage,
  trackArticleTOCClick,
  trackArticleFAQExpand,
  trackArticleCTAClick,
  trackRelatedArticleClick,
  trackKennisbankView,
  trackCategoryView,
  trackArticleClickFromList,
  // Helpers
  createScrollDepthTracker,
  createTimeOnPageTracker,
};

export default analytics;